import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { approveCapacity } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle, ShieldCheck } from '@phosphor-icons/react';
import { useOwnerFetch, OwnerUnlockPrompt } from '../lib/ownerFetch';

export default function ApproveCapacity() {
  const navigate = useNavigate();
  const [tripCode, setTripCode] = useState('');
  const [roundsDelta, setRoundsDelta] = useState(0);
  const [playersDelta, setPlayersDelta] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const { ownerFetch, unlockProps } = useOwnerFetch();

  const canSubmit = tripCode.trim().length > 0 && (roundsDelta > 0 || playersDelta > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    const res = await ownerFetch(() => approveCapacity(tripCode.trim().toUpperCase(), {
      rounds_delta: roundsDelta,
      players_delta: playersDelta,
    }));
    if (res.ok) {
      setResult(res.data);
    } else if (res.error) {
      // Translate known codes to user-facing text
      if (res.error === 'invalid_owner_password') setError('Owner password incorrect.');
      else if (res.error === 'trip_owner_password_not_set') setError('This trip has no owner password set — generate one first.');
      else if (res.error === 'legacy_trip_no_caps_to_approve') setError('This is a legacy trip — no cap approval needed.');
      else if (res.error === 'Trip not found') setError('Trip not found. Check the code.');
      else setError(res.error);
    }
    setSubmitting(false);
  };

  const handleReset = () => {
    setTripCode('');
    setRoundsDelta(0);
    setPlayersDelta(0);
    setError('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="approve-capacity-page">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/home')} className="text-white p-1" data-testid="approve-back-btn">
          <ArrowLeft className="w-5 h-5" weight="bold" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl text-white font-bold">Approve Capacity</h1>
          <p className="text-xs text-[#B8C7C0]">Increase round or player caps for a trip</p>
        </div>
        <ShieldCheck className="w-6 h-6 text-[#E59000]" weight="fill" />
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto">
        {result ? (
          <Card className="p-6 bg-white border-[#C8E6C9] rounded-xl space-y-4" data-testid="approve-success">
            <div className="flex items-center gap-2 text-[#2E7D32]">
              <CheckCircle className="w-6 h-6" weight="fill" />
              <span className="font-bold text-lg">Capacity Approved</span>
            </div>
            <div className="bg-[#E8F5E9] rounded-lg p-4 space-y-1">
              <p className="text-sm text-[#2E7D32]">New max rounds: <strong>{result.new_max_rounds}</strong></p>
              <p className="text-sm text-[#2E7D32]">New max players: <strong>{result.new_max_players}</strong></p>
              <p className="text-xs text-[#4CAF50] mt-2">Approval recorded ({result.approvals_count} total)</p>
            </div>
            <Button onClick={handleReset}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
              data-testid="approve-another-btn">
              Approve Another
            </Button>
          </Card>
        ) : (
          <Card className="p-6 bg-white border-[#E1E6E4] rounded-xl space-y-5" data-testid="approve-form">
            <div>
              <label className="text-sm font-semibold text-[#455561] block mb-1.5">Event code</label>
              <Input value={tripCode}
                onChange={(e) => { setTripCode(e.target.value); setError(''); }}
                onBlur={() => setTripCode(tripCode.trim().toUpperCase())}
                placeholder="e.g. OBS40Q" className="h-12 rounded-xl text-base font-mono"
                autoComplete="off" data-testid="approve-trip-code" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-[#455561] block mb-1.5">Rounds +</label>
                <Input type="number" min={0} value={roundsDelta}
                  onChange={(e) => { setRoundsDelta(Math.max(0, parseInt(e.target.value) || 0)); setError(''); }}
                  className="h-12 rounded-xl text-base" data-testid="approve-rounds-delta" />
              </div>
              <div>
                <label className="text-sm font-semibold text-[#455561] block mb-1.5">Players +</label>
                <Input type="number" min={0} value={playersDelta}
                  onChange={(e) => { setPlayersDelta(Math.max(0, parseInt(e.target.value) || 0)); setError(''); }}
                  className="h-12 rounded-xl text-base" data-testid="approve-players-delta" />
              </div>
            </div>
            {error && <p className="text-sm text-[#D32F2F] text-center" data-testid="approve-error">{error}</p>}
            <OwnerUnlockPrompt {...unlockProps} />
            <Button onClick={handleSubmit} disabled={submitting || !canSubmit || unlockProps.ownerExpired}
              className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-bold"
              data-testid="approve-submit-btn">
              {submitting ? 'Approving...' : 'Approve Increase'}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
